// src/pages/UserForm.jsx

import React, { useState, useEffect } from "react";
import { useForm } from "@mantine/form";
import {
  Modal,
  TextInput,
  PasswordInput,
  Select,
  Button,
  LoadingOverlay,
  Alert,
  Group,
  SimpleGrid,
  Text,
  Divider,
} from "@mantine/core";
import api from "../../services/api";

/**
 * Props:
 * - opened: boolean
 * - onClose: function
 * - onSuccess: function
 * - user: object | null ('null' untuk Tambah, 'object' untuk Edit)
 */
export default function UserForm({ opened, onClose, onSuccess, user }) {
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRoleName, setSelectedRoleName] = useState("");

  // Data Wilayah
  const [kotaList, setKotaList] = useState([]);
  const [kecamatanList, setKecamatanList] = useState([]);
  const [kelurahanList, setKelurahanList] = useState([]);

  // 1. Tentukan mode (Tambah / Edit)
  const isEdit = Boolean(user);

  // 2. Definisikan semua field di initialValues
  const form = useForm({
    initialValues: {
      // User
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
      role_id: "",
      // Profil (Menggunakan nama kolom DB yang disederhanakan: alamat dan no_telp)
      npsn: "",
      nama_sekolah: "",
      jenis_sekolah: "",
      kelurahan_id: "",
      alamat: "",
      no_telp: "",
      nama_perusahaan: "",
      kota_id: "",
      kecamatan_id: "",
    },
    validate: {
      name: (val) =>
        val.trim().length === 0 ? "Nama tidak boleh kosong" : null,
      email: (val) => (/^\S+@\S+$/.test(val) ? null : "Email tidak valid"),
      role_id: (val) => (val === "" ? "Role harus dipilih" : null),
      password: (val) =>
        !isEdit && val.length < 8 ? "Password minimal 8 karakter" : null,

      // Validasi dinamis
      npsn: (val) =>
        selectedRoleName === "sekolah" && !val
          ? "NPSN tidak boleh kosong"
          : null,
      nama_sekolah: (val) =>
        selectedRoleName === "sekolah" && !val
          ? "Nama sekolah tidak boleh kosong"
          : null,
      jenis_sekolah: (val) =>
        selectedRoleName === "sekolah" && !val
          ? "Jenis sekolah tidak boleh kosong"
          : null,
      kelurahan_id: (val) =>
        selectedRoleName === "sekolah" && !val
          ? "Kelurahan tidak boleh kosong"
          : null,
      alamat: (val) =>
        (selectedRoleName === "sekolah" || selectedRoleName === "penyedia") &&
        !val
          ? "Alamat tidak boleh kosong"
          : null,
      no_telp: (val) =>
        selectedRoleName === "penyedia" && !val
          ? "Nomor telepon tidak boleh kosong"
          : null,
      nama_perusahaan: (val) =>
        selectedRoleName === "penyedia" && !val
          ? "Nama perusahaan tidak boleh kosong"
          : null,
    },
  });

  // ==========================================================
  // --- FUNGSI FETCH DATA (Dibutuhkan untuk useEffect) ---
  // ==========================================================
  const fetchRoles = async () => {
    try {
      const res = await api.get("/admin/form-data/roles");
      const formattedRoles = res.data.map((r) => ({
        value: r.id.toString(),
        label: r.nama_role,
      }));
      setRoles(formattedRoles);
    } catch (e) {
      console.error("Gagal fetch roles", e);
    }
  };

  const fetchKota = async () => {
    try {
      const res = await api.get("/wilayah/kota");
      const formattedKota = res.data.map((k) => ({
        value: k.id.toString(),
        label: k.nama_kota,
      }));
      setKotaList(formattedKota);
    } catch (e) {
      console.error("Gagal fetch kota", e);
    }
  };

  // Fetch Kecamatan saat Kota berubah
  useEffect(() => {
    if (form.values.kota_id) {
      api.get(`/wilayah/kecamatan/${form.values.kota_id}`).then((res) => {
        setKecamatanList(
          res.data.map((k) => ({
            value: k.id.toString(),
            label: k.nama_kecamatan,
          }))
        );
      });
    } else {
      setKecamatanList([]);
    }
    form.setFieldValue("kecamatan_id", "");
    form.setFieldValue("kelurahan_id", "");
  }, [form.values.kota_id]);

  // Fetch Kelurahan saat Kecamatan berubah
  useEffect(() => {
    if (form.values.kecamatan_id) {
      api.get(`/wilayah/kelurahan/${form.values.kecamatan_id}`).then((res) => {
        setKelurahanList(
          res.data.map((k) => ({
            value: k.id.toString(),
            label: k.nama_kelurahan,
          }))
        );
      });
    } else {
      setKelurahanList([]);
    }
    form.setFieldValue("kelurahan_id", "");
  }, [form.values.kecamatan_id]);

  // ==========================================================
  // --- FUNGSI HANDLE PERUBAHAN & PENUTUPAN (Dibutuhkan) ---
  // ==========================================================

  const handleRoleChange = (value) => {
    form.setFieldValue("role_id", value);
    const role = roles.find((r) => r.value === value);
    setSelectedRoleName(role ? role.label : "");
  };

  const handleClose = () => {
    form.reset();
    setFormError(null);
    setSelectedRoleName("");
    onClose();
  };

  // ==========================================================
  // --- FUNGSI MENGISI FORM SAAT 'EDIT' ---
  // ==========================================================
  useEffect(() => {
    if (opened) {
      // Panggil fungsi yang sebelumnya di-error-kan
      fetchRoles();
      fetchKota();

      if (isEdit) {
        // --- MODE EDIT ---
        form.setValues({
          name: user.name,
          email: user.email,
          role_id: user.role_id.toString(),
          password: "",
          password_confirmation: "",
        });

        const roleName = user.role.nama_role;
        setSelectedRoleName(roleName);

        if (roleName === "sekolah" && user.sekolah) {
          form.setValues({
            npsn: user.sekolah.npsn,
            nama_sekolah: user.sekolah.nama_sekolah,
            alamat: user.sekolah.alamat, // 'alamat' tunggal
            jenis_sekolah: user.sekolah.jenis_sekolah,
            kelurahan_id: user.sekolah.kelurahan_id.toString(),
          });
        } else if (roleName === "penyedia" && user.penyedia) {
          form.setValues({
            nama_perusahaan: user.penyedia.nama_perusahaan,
            alamat: user.penyedia.alamat, // 'alamat' tunggal
            no_telp: user.penyedia.no_telp,
          });
        }
      } else {
        // --- MODE TAMBAH ---
        form.reset();
        setSelectedRoleName("");
      }
    }
  }, [user, opened]);

  // ==========================================================
  // --- LOGIKA 'handleSubmit' ---
  // ==========================================================
  const handleSubmit = async (values) => {
    setLoading(true);
    setFormError(null);

    const dataToSubmit = { ...values };
    delete dataToSubmit.kota_id;
    delete dataToSubmit.kecamatan_id;

    if (isEdit && dataToSubmit.password === "") {
      delete dataToSubmit.password;
      delete dataToSubmit.password_confirmation;
    }

    try {
      if (isEdit) {
        // PERINGATAN: Fungsi 'update' di controller Anda mungkin belum menangani profil!
        await api.put(`/admin/users/${user.id}`, dataToSubmit);
      } else {
        await api.post("/admin/users", dataToSubmit);
      }
      onSuccess();
      handleClose();
    } catch (err) {
      if (err.response && err.response.status === 422) {
        setFormError(Object.values(err.response.data.errors).flat().join(", "));
      } else {
        setFormError("Gagal menyimpan data. Coba lagi.");
      }
    } finally {
      setLoading(false);
    }
  };

  // --- RENDER UI DINAMIS (Fungsi Helper) ---
  const renderProfilSekolah = () => (
    <>
      <Divider label="Data Profil Sekolah" my="md" />
      <SimpleGrid cols={2}>
        <TextInput label="NPSN" {...form.getInputProps("npsn")} required />
        <TextInput
          label="Nama Sekolah"
          {...form.getInputProps("nama_sekolah")}
          required
        />
      </SimpleGrid>

      {/* Menggunakan 'alamat' tunggal */}
      <TextInput
        label="Alamat Sekolah"
        {...form.getInputProps("alamat")}
        mt="md"
        required
      />

      <Select
        label="Jenis Sekolah"
        data={[
          { value: "SD", label: "SD" },
          { value: "MI", label: "MI" },
          { value: "SMP", label: "SMP" },
          { value: "MTS", label: "MTS" },
          { value: "SMA", label: "SMA" },
          { value: "SMK", label: "SMK" },
          { value: "MA", label: "MA" },
          { value: "SLB", label: "SLB" },
        ]}
        {...form.getInputProps("jenis_sekolah")}
        mt="md"
        required
      />

      <Text fw={500} size="sm" mt="md">
        Wilayah
      </Text>
      <SimpleGrid cols={3} mt="xs">
        <Select
          label="Kota"
          data={kotaList}
          {...form.getInputProps("kota_id")}
          searchable
        />
        <Select
          label="Kecamatan"
          data={kecamatanList}
          {...form.getInputProps("kecamatan_id")}
          searchable
          disabled={!form.values.kota_id}
        />
        <Select
          label="Kelurahan"
          data={kelurahanList}
          {...form.getInputProps("kelurahan_id")}
          searchable
          disabled={!form.values.kecamatan_id}
          required
        />
      </SimpleGrid>
    </>
  );

  const renderProfilPenyedia = () => (
    <>
      <Divider label="Data Profil Penyedia" my="md" />
      <TextInput
        label="Nama Perusahaan"
        {...form.getInputProps("nama_perusahaan")}
        required
      />

      {/* Menggunakan 'alamat' dan 'no_telp' tunggal */}
      <TextInput
        label="Alamat Penyedia"
        {...form.getInputProps("alamat")}
        mt="md"
        required
      />
      <TextInput
        label="No. Telepon"
        {...form.getInputProps("no_telp")}
        mt="md"
        required
      />
    </>
  );

  // --- Tampilan Modal ---
  return (
    <Modal
      opened={opened}
      onClose={handleClose} // Didefinisikan di atas
      title={isEdit ? `Edit User: ${user.name}` : "Tambah User Baru"}
      size="lg"
      overlayProps={{ blur: 2 }}
      scrollable
    >
      <LoadingOverlay visible={loading} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        {formError && (
          <Alert
            color="red"
            title="Error"
            withCloseButton
            onClose={() => setFormError(null)}
            mb="md"
          >
            {formError}
          </Alert>
        )}

        <Text fw={700} mb="xs">
          Data Akun
        </Text>
        <SimpleGrid cols={2}>
          <TextInput
            label="Nama Lengkap"
            {...form.getInputProps("name")}
            required
          />
          <TextInput label="Email" {...form.getInputProps("email")} required />
        </SimpleGrid>

        <SimpleGrid cols={2} mt="md">
          <PasswordInput
            label={isEdit ? "Password Baru (Opsional)" : "Password"}
            {...form.getInputProps("password")}
            required={!isEdit}
          />
          <PasswordInput
            label={isEdit ? "Konfirmasi Password Baru" : "Konfirmasi Password"}
            {...form.getInputProps("password_confirmation")}
            required={!isEdit}
          />
        </SimpleGrid>

        <Select
          label="Role"
          data={roles}
          {...form.getInputProps("role_id")}
          onChange={handleRoleChange} // Didefinisikan di atas
          required
          mt="md"
        />

        {selectedRoleName === "sekolah" && renderProfilSekolah()}
        {selectedRoleName === "penyedia" && renderProfilPenyedia()}

        <Group justify="flex-end" mt="xl">
          <Button variant="default" onClick={handleClose}>
            {" "}
            Batal{" "}
          </Button>
          <Button type="submit" loading={loading}>
            {" "}
            Simpan{" "}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
